/**
 * Meeting Tools — Distraction Report.
 *
 * Views (pill switch):
 *   - All distraction: type-level rows for attendees
 *   - Student wise: attendee list → click name for detail
 *
 * Absentees are excluded from both views.
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  fetchDistractionReport,
  type DistractionReportStudent,
  type DistractionReportSummary,
} from "./distractionReportApi";
import {
  isAuthHttpError,
  isAuthSessionExpired,
} from "../utils/authSessionGuard";

type Props = {
  sessionId: string;
  pollMs?: number;
};

type ViewId = "all" | "students";

type DistractionRow = {
  key: string;
  studentId: string;
  studentName: string;
  distractionType: string;
  count: number;
  durationLabel: string;
  sortScore: number;
};

type StudentAggregate = {
  student: DistractionReportStudent;
  totalCount: number;
  totalSeconds: number;
};

const emptySummary: DistractionReportSummary = {
  students_tracked: 0,
  tab_switches: 0,
  tab_switch_seconds: 0,
  tab_switch_minutes: 0,
  minimize_count: 0,
  minimize_seconds: 0,
  minimize_minutes: 0,
  alert_popup_events: 0,
  alert_popup_seconds: 0,
  alert_popup_minutes: 0,
  missed_quizzes: 0,
  poll_non_responses: 0,
  completed_quizzes: 0,
  ended_polls: 0,
};

function formatDuration(minutes: number, seconds: number): string {
  // Prefer raw seconds — minutes are rounded and can drop short durations to "—".
  const sec =
    Number.isFinite(seconds) && seconds > 0
      ? seconds
      : Number.isFinite(minutes) && minutes > 0
        ? minutes * 60
        : 0;
  return formatSeconds(sec);
}

function formatSeconds(totalSeconds: number): string {
  const sec = Math.max(0, Math.round(totalSeconds || 0));
  if (sec <= 0) return "—";
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const r = sec % 60;
  return r > 0 ? `${m}m ${r}s` : `${m}m`;
}

function formatAttendedMinutes(minutes: number): string {
  const m = Math.max(0, Math.round(minutes || 0));
  if (m <= 0) return "—";
  return `${m}m`;
}

/** Total distraction time / attended time */
function formatDistractionOverAttended(
  distractionSeconds: number,
  attendedMinutes: number,
): string {
  const left = formatSeconds(distractionSeconds);
  const right = formatAttendedMinutes(attendedMinutes);
  if (left === "—" && right === "—") return "—";
  return `${left} / ${right}`;
}

function totalDistractionCount(s: DistractionReportStudent): number {
  return (
    (s.tab_switches || 0) +
    (s.minimize_count || 0) +
    (s.alert_popup_events || 0) +
    (s.missed_quizzes || 0) +
    (s.poll_non_responses || 0)
  );
}

function totalDistractionSeconds(s: DistractionReportStudent): number {
  const raw =
    (s.tab_switch_seconds || 0) +
    (s.minimize_seconds || 0) +
    (s.alert_popup_seconds || 0);
  const attendedSec = Math.max(0, (s.attended_minutes || 0) * 60);
  if (attendedSec > 0 && raw > attendedSec) return attendedSec;
  return raw;
}

function buildTypeRows(students: DistractionReportStudent[]): DistractionRow[] {
  const rows: DistractionRow[] = [];
  for (const s of students) {
    if (s.tab_switches > 0 || s.tab_switch_seconds > 0) {
      rows.push({
        key: `${s.student_id}:tab`,
        studentId: s.student_id,
        studentName: s.name,
        distractionType: "Tab switch",
        count: s.tab_switches,
        durationLabel: formatDuration(
          s.tab_switch_minutes,
          s.tab_switch_seconds,
        ),
        sortScore: s.tab_switches + s.tab_switch_seconds,
      });
    }
    if (s.minimize_count > 0 || s.minimize_seconds > 0) {
      rows.push({
        key: `${s.student_id}:mini`,
        studentId: s.student_id,
        studentName: s.name,
        distractionType: "Minimize window",
        count: s.minimize_count,
        durationLabel: formatDuration(s.minimize_minutes, s.minimize_seconds),
        sortScore: s.minimize_count + s.minimize_seconds,
      });
    }
    if (s.alert_popup_events > 0 || s.alert_popup_seconds > 0) {
      rows.push({
        key: `${s.student_id}:alert`,
        studentId: s.student_id,
        studentName: s.name,
        distractionType: "Alert pop-up",
        count: s.alert_popup_events,
        durationLabel: formatDuration(s.alert_popup_minutes, s.alert_popup_seconds),
        sortScore: s.alert_popup_events + s.alert_popup_seconds,
      });
    }
    if (s.missed_quizzes > 0) {
      rows.push({
        key: `${s.student_id}:quiz`,
        studentId: s.student_id,
        studentName: s.name,
        distractionType: "Missed quiz",
        count: s.missed_quizzes,
        durationLabel: "—",
        sortScore: s.missed_quizzes,
      });
    }
    if (s.poll_non_responses > 0) {
      rows.push({
        key: `${s.student_id}:poll`,
        studentId: s.student_id,
        studentName: s.name,
        distractionType: "Poll non-response",
        count: s.poll_non_responses,
        durationLabel: "—",
        sortScore: s.poll_non_responses,
      });
    }
  }
  rows.sort(
    (a, b) =>
      a.studentName.localeCompare(b.studentName) ||
      b.sortScore - a.sortScore ||
      a.distractionType.localeCompare(b.distractionType),
  );
  return rows;
}

function FilterChip({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        border: "none",
        borderRadius: 999,
        padding: "6px 12px",
        fontSize: 12,
        fontWeight: 600,
        cursor: "pointer",
        background: active ? "#2563eb" : "rgba(255,255,255,0.06)",
        color: active ? "#fff" : "#94a3b8",
      }}
    >
      {label}
    </button>
  );
}

function SummaryCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div
      style={{
        flex: "1 1 120px",
        minWidth: 0,
        borderRadius: 10,
        border: "1px solid rgba(255,255,255,0.08)",
        background: "rgba(255,255,255,0.03)",
        padding: "10px 12px",
      }}
    >
      <p
        style={{
          margin: 0,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 0.4,
          color: "#64748b",
          textTransform: "uppercase",
        }}
      >
        {label}
      </p>
      <p
        style={{
          margin: "6px 0 0",
          fontSize: 16,
          fontWeight: 700,
          color: "#e2e8f0",
        }}
      >
        {value}
      </p>
      {sub ? (
        <p style={{ margin: "4px 0 0", fontSize: 11, color: "#94a3b8" }}>{sub}</p>
      ) : null}
    </div>
  );
}

function DetailRow({
  label,
  value,
  strong,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
      }}
    >
      <span style={{ color: "#94a3b8" }}>{label}</span>
      <span
        style={{
          color: strong ? "#e2e8f0" : "#cbd5e1",
          fontWeight: strong ? 700 : 500,
          textAlign: "right",
        }}
      >
        {value}
      </span>
    </div>
  );
}

function StudentDetailView({
  student,
  onBack,
}: {
  student: DistractionReportStudent;
  onBack: () => void;
}) {
  const totalCount = totalDistractionCount(student);
  const totalSec = totalDistractionSeconds(student);
  const typeRows = buildTypeRows([student]);

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        gap: 12,
        overflow: "hidden",
      }}
    >
      <button
        type="button"
        onClick={onBack}
        style={{
          alignSelf: "flex-start",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: 8,
          background: "rgba(255,255,255,0.04)",
          color: "#e2e8f0",
          fontSize: 12,
          fontWeight: 600,
          padding: "6px 10px",
          cursor: "pointer",
        }}
      >
        <span aria-hidden style={{ fontSize: 14, lineHeight: 1 }}>
          ←
        </span>
        Back to students
      </button>

      <div
        className="meeting-tool-hide-scrollbar"
        style={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          borderRadius: 10,
          border: "1px solid rgba(255,255,255,0.08)",
          background: "rgba(255,255,255,0.03)",
          padding: 16,
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#f8fafc" }}>
          {student.name}
        </p>
        <div
          style={{
            marginTop: 14,
            display: "grid",
            gap: 10,
            fontSize: 13,
          }}
        >
          <DetailRow
            label="Total distraction count"
            value={String(totalCount)}
            strong
          />
          <DetailRow
            label="Total distraction time / attended"
            value={formatDistractionOverAttended(
              totalSec,
              student.attended_minutes,
            )}
            strong
          />
        </div>

        <div
          style={{
            marginTop: 16,
            paddingTop: 14,
            borderTop: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <p
            style={{
              margin: "0 0 10px",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 0.4,
              color: "#64748b",
              textTransform: "uppercase",
            }}
          >
            Distraction details
          </p>
          {typeRows.length === 0 ? (
            <p style={{ margin: 0, fontSize: 13, color: "#94a3b8" }}>
              No distraction events for this student.
            </p>
          ) : (
            <div style={{ display: "grid", gap: 10, fontSize: 13 }}>
              {typeRows.map((row) => (
                <DetailRow
                  key={row.key}
                  label={`${row.distractionType} (${row.count}×)`}
                  value={row.durationLabel}
                  strong
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function stickyTh(label: string) {
  return (
    <th
      style={{
        padding: "10px 8px",
        fontWeight: 700,
        fontSize: 10,
        letterSpacing: 0.3,
        textTransform: "uppercase",
        borderBottom: "1px solid rgba(255,255,255,0.08)",
        position: "sticky",
        top: 0,
        background: "#141820",
        color: "#64748b",
        textAlign: "left",
      }}
    >
      {label}
    </th>
  );
}

export function DistractionReportPanel({ sessionId, pollMs }: Props) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState<DistractionReportSummary>(emptySummary);
  const [students, setStudents] = useState<DistractionReportStudent[]>([]);
  const [view, setView] = useState<ViewId>("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Live refresh: faster while a student detail is open so times/counts update as they change.
  const effectivePollMs =
    pollMs ?? (selectedId ? 3_000 : 5_000);

  useEffect(() => {
    let cancelled = false;
    let inFlight = false;
    let intervalId: number | undefined;

    const stop = () => {
      cancelled = true;
      if (intervalId != null) {
        window.clearInterval(intervalId);
        intervalId = undefined;
      }
    };

    const load = async () => {
      if (inFlight || cancelled || isAuthSessionExpired()) {
        if (isAuthSessionExpired()) stop();
        return;
      }
      inFlight = true;
      try {
        const report = await fetchDistractionReport(sessionId);
        if (cancelled) return;
        setSummary(report.summary);
        setStudents(report.students);
        setError("");
        setSelectedId((prev) => {
          if (!prev) return null;
          const stillThere = report.students.some(
            (s) => s.student_id === prev,
          );
          return stillThere ? prev : null;
        });
      } catch (err) {
        if (cancelled) return;
        if (isAuthHttpError(err) || isAuthSessionExpired()) {
          stop();
          return;
        }
        console.error("distraction-report load failed", err);
        setError("Could not load distraction report.");
      } finally {
        inFlight = false;
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    intervalId = window.setInterval(() => void load(), effectivePollMs);

    const onVisible = () => {
      if (document.visibilityState === "visible") void load();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [sessionId, effectivePollMs]);

  const attendees = useMemo(
    () =>
      students.filter((s) => {
        if (s.present || s.active) return true;
        // Fallback: show anyone who already has distraction telemetry.
        return (
          (s.tab_switches || 0) > 0 ||
          (s.tab_switch_seconds || 0) > 0 ||
          (s.minimize_count || 0) > 0 ||
          (s.minimize_seconds || 0) > 0 ||
          (s.alert_popup_events || 0) > 0 ||
          (s.alert_popup_seconds || 0) > 0 ||
          (s.missed_quizzes || 0) > 0 ||
          (s.poll_non_responses || 0) > 0
        );
      }),
    [students],
  );

  const attendeeSummary = useMemo(() => {
    const tab = attendees.reduce((n, s) => n + (s.tab_switches || 0), 0);
    const tabSec = attendees.reduce((n, s) => n + (s.tab_switch_seconds || 0), 0);
    const mini = attendees.reduce((n, s) => n + (s.minimize_count || 0), 0);
    const miniSec = attendees.reduce((n, s) => n + (s.minimize_seconds || 0), 0);
    const alertEv = attendees.reduce((n, s) => n + (s.alert_popup_events || 0), 0);
    const alertSec = attendees.reduce((n, s) => n + (s.alert_popup_seconds || 0), 0);
    const missed = attendees.reduce((n, s) => n + (s.missed_quizzes || 0), 0);
    const polls = attendees.reduce((n, s) => n + (s.poll_non_responses || 0), 0);
    return {
      attendees: attendees.length,
      tab_switches: tab,
      tab_switch_seconds: tabSec,
      tab_switch_minutes: Math.round((tabSec / 60) * 10) / 10,
      minimize_count: mini,
      minimize_seconds: miniSec,
      minimize_minutes: Math.round((miniSec / 60) * 10) / 10,
      alert_popup_events: alertEv,
      alert_popup_seconds: alertSec,
      alert_popup_minutes: Math.round((alertSec / 60) * 10) / 10,
      missed_quizzes: missed,
      poll_non_responses: polls,
      completed_quizzes: summary.completed_quizzes,
      ended_polls: summary.ended_polls,
    };
  }, [attendees, summary.completed_quizzes, summary.ended_polls]);

  const typeRows = useMemo(() => buildTypeRows(attendees), [attendees]);

  const studentRows = useMemo((): StudentAggregate[] => {
    return attendees
      .map((student) => ({
        student,
        totalCount: totalDistractionCount(student),
        totalSeconds: totalDistractionSeconds(student),
      }))
      .filter((row) => row.totalCount > 0 || row.totalSeconds > 0)
      .sort(
        (a, b) =>
          b.totalCount - a.totalCount ||
          b.totalSeconds - a.totalSeconds ||
          a.student.name.localeCompare(b.student.name),
      );
  }, [attendees]);

  const selected = useMemo(
    () => attendees.find((s) => s.student_id === selectedId) || null,
    [attendees, selectedId],
  );

  if (loading && students.length === 0) {
    return (
      <p style={{ margin: 0, color: "#94a3b8", fontSize: 13 }}>
        Loading distraction report…
      </p>
    );
  }

  if (error && students.length === 0) {
    return (
      <p style={{ margin: 0, color: "#f87171", fontSize: 13 }}>{error}</p>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 14,
        minHeight: 0,
        height: "100%",
        color: "#e2e8f0",
      }}
    >
      {selected ? (
        <StudentDetailView
          student={selected}
          onBack={() => setSelectedId(null)}
        />
      ) : (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            <SummaryCard
              label="Attendees tracked"
              value={String(attendeeSummary.attendees)}
            />
            <SummaryCard
              label="Tab switches"
              value={String(attendeeSummary.tab_switches)}
              sub={formatDuration(
                attendeeSummary.tab_switch_minutes,
                attendeeSummary.tab_switch_seconds,
              )}
            />
            <SummaryCard
              label="Minimized"
              value={String(attendeeSummary.minimize_count)}
              sub={formatDuration(
                attendeeSummary.minimize_minutes,
                attendeeSummary.minimize_seconds,
              )}
            />
            <SummaryCard
              label="Alert pop-up"
              value={String(attendeeSummary.alert_popup_events)}
              sub={formatDuration(
                attendeeSummary.alert_popup_minutes,
                attendeeSummary.alert_popup_seconds,
              )}
            />
            <SummaryCard
              label="Missed quizzes"
              value={String(attendeeSummary.missed_quizzes)}
            />
            <SummaryCard
              label="Poll non-responses"
              value={String(attendeeSummary.poll_non_responses)}
            />
          </div>

          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <FilterChip
              active={view === "all"}
              label="All distraction"
              onClick={() => {
                setView("all");
                setSelectedId(null);
              }}
            />
            <FilterChip
              active={view === "students"}
              label="Student wise"
              onClick={() => {
                setView("students");
                setSelectedId(null);
              }}
            />
          </div>

          {error ? (
            <p style={{ margin: 0, fontSize: 12, color: "#f87171" }}>{error}</p>
          ) : null}

          <div
            className="meeting-tool-hide-scrollbar"
            style={{
              flex: 1,
              minHeight: 0,
              overflow: "auto",
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.08)",
              background: "rgba(0,0,0,0.2)",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            {view === "all" ? (
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 12,
                }}
              >
                <thead>
                  <tr>
                    {stickyTh("Student name")}
                    {stickyTh("Distraction type")}
                    {stickyTh("No. of times")}
                    {stickyTh("Duration")}
                  </tr>
                </thead>
                <tbody>
                  {typeRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={4}
                        style={{
                          padding: 16,
                          color: "#94a3b8",
                          textAlign: "center",
                        }}
                      >
                        No distraction events for attendees yet.
                      </td>
                    </tr>
                  ) : (
                    typeRows.map((row) => (
                      <tr
                        key={row.key}
                        style={{
                          borderTop: "1px solid rgba(255,255,255,0.06)",
                        }}
                      >
                        <td style={{ padding: "10px 8px" }}>
                          <button
                            type="button"
                            onClick={() => setSelectedId(row.studentId)}
                            style={{
                              border: "none",
                              background: "transparent",
                              padding: 0,
                              margin: 0,
                              color: "#60a5fa",
                              fontWeight: 700,
                              fontSize: 12,
                              cursor: "pointer",
                              textAlign: "left",
                              textDecoration: "underline",
                              textUnderlineOffset: 2,
                            }}
                          >
                            {row.studentName}
                          </button>
                        </td>
                        <td style={{ padding: "10px 8px", color: "#cbd5e1" }}>
                          {row.distractionType}
                        </td>
                        <td
                          style={{
                            padding: "10px 8px",
                            fontWeight: 600,
                            color: row.count > 0 ? "#f87171" : "#94a3b8",
                          }}
                        >
                          {row.count}
                        </td>
                        <td style={{ padding: "10px 8px", color: "#cbd5e1" }}>
                          {row.durationLabel}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            ) : (
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 12,
                }}
              >
                <thead>
                  <tr>
                    {stickyTh("Student name")}
                    {stickyTh("Total distraction")}
                    {stickyTh("Total distraction time / attended")}
                  </tr>
                </thead>
                <tbody>
                  {studentRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={3}
                        style={{
                          padding: 16,
                          color: "#94a3b8",
                          textAlign: "center",
                        }}
                      >
                        No attendees with distraction events yet.
                      </td>
                    </tr>
                  ) : (
                    studentRows.map(({ student, totalCount, totalSeconds }) => (
                      <tr
                        key={student.student_id}
                        style={{
                          borderTop: "1px solid rgba(255,255,255,0.06)",
                        }}
                      >
                        <td style={{ padding: "10px 8px" }}>
                          <button
                            type="button"
                            onClick={() => setSelectedId(student.student_id)}
                            style={{
                              border: "none",
                              background: "transparent",
                              padding: 0,
                              margin: 0,
                              color: "#60a5fa",
                              fontWeight: 700,
                              fontSize: 12,
                              cursor: "pointer",
                              textAlign: "left",
                              textDecoration: "underline",
                              textUnderlineOffset: 2,
                            }}
                          >
                            {student.name}
                          </button>
                        </td>
                        <td
                          style={{
                            padding: "10px 8px",
                            fontWeight: 600,
                            color: totalCount > 0 ? "#f87171" : "#94a3b8",
                          }}
                        >
                          {totalCount}
                        </td>
                        <td style={{ padding: "10px 8px", color: "#cbd5e1" }}>
                          {formatDistractionOverAttended(
                            totalSeconds,
                            student.attended_minutes,
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
